/**
 * ErrorState — Apple-style error display with retry.
 *
 * Clean centered layout: icon circle, title, message, button.
 * No decorative gradients or colored shadows — Apple HIG minimal.
 */

import { AlertTriangle, RefreshCw, WifiOff, ServerCrash } from 'lucide-react';
import { ds } from '@/design-system/tokens';
import { Button } from '@/design-system/primitives';

interface ErrorStateProps {
  title?: string;
  message?: string;
  type?: 'network' | 'server' | 'generic';
  onRetry?: () => void;
  retryText?: string;
  className?: string;
}

const ERROR_CONFIG = {
  network: {
    icon: WifiOff,
    defaultTitle: 'No internet connection',
    defaultMessage: 'Please check your internet connection and try again.',
  },
  server: {
    icon: ServerCrash,
    defaultTitle: 'Server error',
    defaultMessage: 'Something went wrong on our end. Please try again later.',
  },
  generic: {
    icon: AlertTriangle,
    defaultTitle: 'Something went wrong',
    defaultMessage: 'An unexpected error occurred. Please try again.',
  },
} as const;

export function ErrorState({
  title,
  message,
  type = 'generic',
  onRetry,
  retryText = 'Try Again',
  className = '',
}: ErrorStateProps) {
  const config = ERROR_CONFIG[type];
  const Icon = config.icon;

  return (
    <div className={`flex items-center justify-center min-h-[60vh] px-6 ${className}`}>
      <div className="text-center max-w-sm">
        {/* Icon */}
        <div
          className={`${ds.icon.hero} ${ds.radius.full} flex items-center justify-center mx-auto mb-6`}
          style={{ backgroundColor: ds.color.raw.systemGray6 }}
        >
          <Icon className={ds.icon.xl} style={{ color: ds.color.raw.systemGray2 }} />
        </div>

        {/* Title */}
        <h3 className={`${ds.type.headline} ${ds.color.label.primary} mb-2`}>
          {title || config.defaultTitle}
        </h3>

        {/* Message */}
        <p className={`${ds.type.body} ${ds.color.label.secondary} mb-8 leading-relaxed`}>
          {message || config.defaultMessage}
        </p>

        {/* Retry */}
        {onRetry && (
          <Button variant="primary" onClick={onRetry}>
            <RefreshCw className={ds.icon.sm} />
            {retryText}
          </Button>
        )}
      </div>
    </div>
  );
}

export default ErrorState;
